import type { NewsFeedFilterCategory } from "@/data-access/news-feeds/queries/news-feed-sidebar-filters-queries";
import { derive } from "@/lib/derive-zustand";
import { getPrettyName } from "@/lib/utils/prettyName";
import useFilterDictionary from "@/store/filter-dictionary/filter-dictionary.slice";
import { useStore } from "zustand";
import type { DynamicFilterItem } from "./dynamic-filters.slice";
import useNewsFeedFilters from "./newsFeed-filter.slice";

export type FilterItemWithLabel = {
  categoryLabel: string;
  label: string;
  total: number;
};

export type FilterDataWithLabels = {
  items: FilterItemWithLabel[];
  isLoading: boolean;
  error: string | null;
};

/**
 * Creates a derived selector for filter data with labels, loading state, and error state
 * @param category - The category to derive data for
 * @returns A derived selector that includes items, loading state, and error state
 */
const createFilterDataWithLabelsSelector = (category: NewsFeedFilterCategory) =>
  derive<FilterDataWithLabels>((get) => {
    const categoryFilter = get(useNewsFeedFilters).filters.get(category);
    const categoryData = categoryFilter?.data;
    const isLoading = categoryFilter?.isLoading ?? false;
    const error = categoryFilter?.error ?? null;

    if (!categoryData) {
      return { items: [], isLoading, error };
    }

    const dictionaryItems =
      get(useFilterDictionary).getDictionaryForCategory(category);

    // Pre-process dictionary into a Map for O(1) lookups instead of O(n) find operations
    const dictionaryMap = new Map(
      dictionaryItems?.map((item) => [item.categoryLabel, item.label]) ?? [],
    );

    // Create items with memoized label resolution
    const items = categoryData.map((item) => ({
      categoryLabel: item.categoryLabel,
      label:
        dictionaryMap.get(item.categoryLabel) ||
        getPrettyName(item.categoryLabel),
      total: item.total,
    }));

    return { items, isLoading, error };
  });

/**
 * Hook to get filter data with labels, loading state, and error state for a specific category
 * @param category - The category to get filter data for
 * @returns Object with items, loading state, and error state
 */
export const useNewsFeedFilterDataWithLabels = (
  category: NewsFeedFilterCategory,
): FilterDataWithLabels => {
  const selector = createFilterDataWithLabelsSelector(category);
  return useStore(selector);
};

/**
 * Derives all available narrative cluster filters as an array
 */
const deriveNarrativeClusterFilters = derive<DynamicFilterItem[]>((get) => {
  const narrativeClusterData =
    get(useNewsFeedFilters).dynamicFilters.get("NARRATIVE_CLUSTER");

  return narrativeClusterData?.items ?? [];
});

/**
 * Hook to get all available narrative cluster filters as an array
 * @returns Array of narrative cluster filter items
 */
export const useNarrativeClusterFilters = (): DynamicFilterItem[] => {
  return useStore(deriveNarrativeClusterFilters);
};
